//initial redux
import { put, call, takeEvery, fork, all, take, select, takeLatest } from "redux-saga/effects";
import * as router from "react-router-redux";
import { eventChannel, delay } from "redux-saga";
import reportsservice from "../../../../services/reports";
import moment from "moment-timezone";
import pointsservice from "../../../../services/points";
import steppedSeries from "../../../../utils/steppedSeries";
import { seriesColor, unitsservice, pointName, DateTime, array } from "../../../../utils";
import {types as sitetreetypes} from '../../../../services/SiteTree/redux';
import { actions as charttooltipactions } from "../ChartTooltip/redux";
import { actions as seriesColorActions } from "../../../../services/SeriesColor/redux";

const initialState = {};

/////////
//types
export const types = {
	LOAD_DATA: "components/linechart/LOAD_DATA"
};

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
			case types.LOAD_DATA: {
				return state;
			}
			default:
				return state;
		}
	}
};
function configYAxis(units) {
	let newYAxis = {
		title: {
			text: units,
			align: "high",
			rotation: 0,
			margin: 0,
			y: -30,
			offset: 14,
			style: {
				"font-weight": "600",
				"font-size": "15px"
			}
		},
		labels: {
			style: {
				fontSize: "14px",
				color: "#999"
			}
		}
	};

	if (units == "inSession" ) {
		newYAxis.min = 0;
		newYAxis.max = 1;
		newYAxis.height = "20%";
		newYAxis.top = "80%";
	}

	return newYAxis;
}
//returns teh index of the y index with teh specified units.
//if it doesn't exist it creates a new axis with teh units and returns the index for that axis.
function getAxisIndex(chart, units) {
	if (!chart.options) {
		return;
	}
	let axisIndex = 0;
	let symbol = unitsservice.stringToSymbol(units);
	units = symbol ? symbol : units;
	//if the first yaxis doesnt have a value set then
	if (!chart.yAxis[0].userOptions.title) {
		chart.yAxis[0].update(configYAxis(units));
	} else {
		//search for existing with same units and use that, else add a new axis.
		let axisFound = false;
		for (var ii = 0; ii < chart.yAxis.length; ii++) {
			let wkAxis = chart.yAxis[ii];
			if (wkAxis.userOptions.title.text == units) {
				axisFound = true;
				axisIndex = ii;
				break;
			}
		}
		if (!axisFound) {
			axisIndex = chart.yAxis.length;
			chart.addAxis(configYAxis(units));
		}
	}
	return axisIndex;
}

function* addOutcomes(chart, pointDetails, pointData, id) {
	if (!chart.options) {
		return;
	}

	const colors = seriesColor.getSeriesColors();

	let {colorDictionary, colorIndex} = yield select(x => x.seriesColor);

	for (let index = 0; index < pointDetails.length; index++) {
		const point = pointDetails[index];

		let color = colorDictionary[point.name] ? colorDictionary[point.name] : null;
		if(!color) {
			var newColor = seriesColor.getColorForPoint(point.name);
			if(newColor) {
				color = newColor;
			} else {
				color = colors[colorIndex];
				colorDictionary[point.name] = color;
				colorIndex++;
				if(colorIndex >= colors.length) {
					colorIndex = 0;
				}
				yield put(seriesColorActions.newPoint(point.name, color));
			}
		}

		let isSteppedSeries = steppedSeries.isSteppedSeries(point.name, point.description);
		let runTimeSeries = {
			type: "line",
			data: pointData[point.objectID].map(xx => {
				return { x: DateTime.getGMTDate(xx.timestamp).getTime(), y: xx.value, units: point.units };
			}),
			yAxis: getAxisIndex(chart, point.units),
			name: pointName.formatDescription(point),
			color: color,
			pointPadding: 0,
			groupPadding: 0,
			label: point.name,
			borderWidth: 0,
			step: isSteppedSeries ? "left" : undefined
		};
		chart.addSeries(runTimeSeries);
		yield put(charttooltipactions.addSeries(id, runTimeSeries));
	}
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
		yield all([this.loadData()]);
	},

	*loadData() {
		yield takeLatest(types.LOAD_DATA, function*({ payload }) {
			let currentSite = yield select(x => x.sitetree.currentSite);
			if (!currentSite) {
				yield take(sitetreetypes.ON_LOAD);
				currentSite = yield select(x => x.sitetree.currentSite);
			}
			const component = payload.component;
			const chart = payload.chart;
			const id = payload.id;
			while (chart.series.length > 0) {
				chart.series[0].remove(true);
			}
			const ruleInstanceId = component.ruleInstanceId;
			const fromDate = moment(payload.timePeriod.startDate);
			const toDate = moment(payload.timePeriod.endDate);
			const now = moment().tz(currentSite.momentTimezone);

			const inputsGrouped = array.groupBy(component.inputs, x => x.source);

			//add ienergy points
			if (inputsGrouped.IEnergy && inputsGrouped.IEnergy.length) {
				let points = [];

				for (let index = 0; index < inputsGrouped.IEnergy.length; index++) {
					const input = inputsGrouped.IEnergy[index];
					for (let ss = 0; ss < input.values.length; ss++) {
						const value = input.values[ss];
						points.push(value);
					}
				}

				let pointDetails = yield all(
					points.map(point => call(pointsservice.TrendableObjectGetObject, point.value))
				);
				pointDetails = pointDetails.filter(x => !(x instanceof Error));
				if (!pointDetails || !pointDetails.length) {
					return;
				}
				let pointData = yield call(
					pointsservice.TrendableDataGetData,
					pointDetails.map(x => x.objectID).join(","),
					fromDate,
					toDate
				);
				if (pointData instanceof Error || !pointData) {
					return;
				}
				if (Array.isArray(pointData)) {
					pointData = { [points[0].value]: pointData };
				}

				const covs = points.filter(x => x.coV);
				if (covs.length) {
					const previousValues = yield call(
						pointsservice.TrendableDataGetAsAt,
						covs.map(x => x.value).join(","),
						fromDate
					);
					if (previousValues instanceof Error || !previousValues) {
						return;
					}
					//add cov point to start of chart and a second point up to present time to draw a line
					if (Array.isArray(previousValues)) {
						let val = previousValues[0];
						val.timestamp = fromDate.format("YYYY-MM-DD HH:mm:ss");
						pointData[covs[0].value].unshift(val);
						let lastVal = { ...pointData[covs[0].value][pointData[covs[0].value].length - 1] };
						lastVal.timestamp = now.format("YYYY-MM-DD HH:mm:ss");
						pointData[covs[0].value].push(lastVal);
					} else {
						Object.keys(previousValues).map(id => {
							let val = previousValues[id][0];
							val.timestamp = fromDate.format("YYYY-MM-DD HH:mm:ss");
							pointData[id].unshift(val);
							let lastVal = { ...pointData[id][pointData[id].length - 1] };
							lastVal.timestamp = now.format("YYYY-MM-DD HH:mm:ss");
							pointData[id].push(lastVal);
						});
					}
				}

				if (pointDetails && pointData) {
					yield call(addOutcomes, chart, pointDetails, pointData, id);
				}
			}

			if (inputsGrouped.RuleInstance && inputsGrouped.RuleInstance.length) {
				for (let index = 0; index < inputsGrouped.RuleInstance.length; index++) {
					const wkInput = inputsGrouped.RuleInstance[index];
					for (let vi = 0; vi < wkInput.values.length; vi++) {
						const wkVal = wkInput.values[vi];

						const [ruleDetails, ruleData] = yield all([
							call(pointsservice.GetRuleInstance, wkVal.value),
							call(
								pointsservice.GetRuleInstanceData,
								wkVal.value,
								wkInput.subValues[0].values[index].value,
								fromDate,
								toDate
							)
						]);

						if (ruleDetails instanceof Error || !ruleDetails) {
							return;
						}
						if (ruleData instanceof Error || !ruleData) {
							return;
						}
						ruleDetails.objectID = "Rule";
						yield call(addOutcomes, chart, [ruleDetails], { Rule: ruleData }, id);
					}
				}
			}
			if(payload.onLoadComplete){
				payload.onLoadComplete();
			}
		});
	}
};

////////
//actions
export const actions = {
	loadData(component, chart, timePeriod, onLoadComplete, id) {
		return {
			type: types.LOAD_DATA,
			payload: { component, chart, timePeriod, onLoadComplete, id }
		};
	}
};



// WEBPACK FOOTER //
// ./src/components/ReportComponents/Charts/LineChart/redux.js